import { useSelector } from "react-redux";

import { BigNumber, PayableOverrides } from "ethers";

import { ShoppingCartItem } from "modules/Cart/ShoppingCart.interface";

import useCancelCryptoPayment from "./useCancelCryptoPayment";

import { selectChainId } from "redux/slices/metamask";

import { useContract } from "hooks/useContract";
import useLogger from "hooks/useLogger";

import { ERROR_CODES } from "enums/errorCodes";
import { TRANSACTIONS_STATUSES } from "enums/transactionsStatuses";
import { CONTRACTS } from "enums/contracts";
import { PURPOSES_TYPES } from "enums/purposesTypes";
import { CRYPTO_REDEEM_STEPS, PATHS } from "enums/logger/Paths";
import { ACTION_TYPES } from "enums/logger/ActionTypes";

import {
  PaymentHandlerParams,
  PaymentHandlerResponse,
} from "interfaces/PaymentHandler.interface";

import { API_ROUTES } from "config/routes";

import {
  getEstimatedGasPrice,
  getMetamaskMessageError,
  getMetamaskMessageErrorCode,
  isNativeCurrency,
} from "utils/blockchain";
import { captureException } from "utils/errors";

import MintResponse from "services/store/bundling/mintTypes/MintResponse.interface";

import fetcher from "lib/fetchJson";

const contracts = [CONTRACTS.PAYMENT_PROXY_V3];

const useOrderCryptoPayment = (
  purpose = PURPOSES_TYPES.PHYSICAL,
  items?: ShoppingCartItem[],
): ((
  params: PaymentHandlerParams,
) => Promise<PaymentHandlerResponse | null>) => {
  const handleCancel = useCancelCryptoPayment();
  const [paymentProxyContract] = useContract(contracts);
  const chainId = useSelector(selectChainId);
  const log = useLogger();

  return async ({
    currency,
    price,
    formValues,
    captchaToken,
  }: PaymentHandlerParams) => {
    let bundleData: { nonce: string };
    const onError = () => {
      if (!bundleData) {
        return;
      }
      handleCancel(bundleData.nonce);
    };

    try {
      if (!paymentProxyContract) {
        return {
          status: TRANSACTIONS_STATUSES.ERROR,
          errorMsg: "Missing Redeem contract!",
        };
      }

      const {
        data: { gasLimit, ...data },
      } = await fetcher<{
        data: Pick<MintResponse, "txData" | "tokenAddress"> & {
          nonce: string;
          weiPrice: number;
          gasLimit: BigNumber;
        };
      }>(
        `${API_ROUTES.ORDER_PAY}?chainId=${chainId}`,
        {
          items,
          ...(formValues ? formValues : {}),
          paymentToken: currency,
          purpose,
          price,
          captchaToken,
        },
        "POST",
      );
      bundleData = data;
      log({
        path: PATHS.CRYPTO_REDEEM,
        step: CRYPTO_REDEEM_STEPS.PAY,
        data,
        actionType: ACTION_TYPES.USER,
      });
      const gasPrice = await getEstimatedGasPrice();
      const overrides: PayableOverrides = { gasPrice, gasLimit };

      if (isNativeCurrency(data.tokenAddress)) {
        overrides.value = data.weiPrice;
      }

      const args = [...(data.txData ?? []), overrides];

      const tx = await paymentProxyContract.execTransaction(...args);
      log({
        path: PATHS.CRYPTO_REDEEM,
        step: CRYPTO_REDEEM_STEPS.TX_HASH,
        txHash: tx.hash,
        actionType: ACTION_TYPES.USER,
      });
      await tx.wait();

      return {
        status: TRANSACTIONS_STATUSES.SUCCESS,
      };
    } catch (error) {
      onError();
      error.data?.code === ERROR_CODES.REDEEM_PAY_PRICE &&
        document.dispatchEvent(new Event("price-changed"));
      captureException(error);
      log({
        path: PATHS.CRYPTO_REDEEM,
        step: CRYPTO_REDEEM_STEPS.TX_HASH,
        error,
        actionType: ACTION_TYPES.USER,
      });
      return {
        status: TRANSACTIONS_STATUSES.ERROR,
        errorMsg: getMetamaskMessageError(
          error.data ?? error,
          "Something went wrong",
        ),
        errorCode: getMetamaskMessageErrorCode(error.data ?? error),
      };
    }
  };
};

export default useOrderCryptoPayment;
